import { Add as AddIcon, Edit as EditIcon, Sync as SyncIcon, Visibility as ViewIcon } from "@mui/icons-material";
import { Box, Button, Card, CardContent, IconButton } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import LeadCampaignApi from "../../../../../infrastructure/api/leadCampaignApi";
import MarketGrowthDataTable, {
  TABLE_ACTION_ICON_SX,
} from "../components/MarketGrowthDataTable";
import MarketGrowthPageShell from "../components/MarketGrowthPageShell";
import {
  CAMPAIGN_STATUS_OPTIONS,
  CHANNEL_OPTIONS,
  getEnumLabel,
} from "../constants/leadCampaignEnums";
import LeadCampaignFormDialog from "./components/LeadCampaignFormDialog";

const LeadCampaignsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [campaignsRes, groupsRes] = await Promise.all([
        LeadCampaignApi.getCampaigns({ page: 1, pageSize: 100 }),
        LeadCampaignApi.getGroups({ page: 1, pageSize: 100 }),
      ]);
      const campaignsNorm = LeadCampaignApi.normalize(campaignsRes, "بارگذاری کمپین‌ها ناموفق بود.");
      const groupsNorm = LeadCampaignApi.normalize(groupsRes, "بارگذاری گروه‌ها ناموفق بود.");
      if (campaignsNorm.error) {
        setError(campaignsNorm.error);
        setItems([]);
        return;
      }
      setItems(campaignsNorm.data?.items || campaignsNorm.data?.Items || []);
      setGroups(groupsNorm.data?.items || groupsNorm.data?.Items || []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const tableRows = useMemo(
    () =>
      items.map((row) => ({
        ...row,
        groupTitle: row.groupTitle || String(row.groupId),
        channelLabel: getEnumLabel(CHANNEL_OPTIONS, row.channel),
        statusLabel: getEnumLabel(CAMPAIGN_STATUS_OPTIONS, row.status),
      })),
    [items]
  );

  const headCells = useMemo(
    () => [
      { id: "title", label: "عنوان" },
      { id: "groupTitle", label: "گروه" },
      { id: "channelLabel", label: "کانال" },
      { id: "statusLabel", label: "وضعیت", type: "chip" },
    ],
    []
  );

  const tableActions = useMemo(
    () => [
      {
        icon: <ViewIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => navigate(`/SLN/MarketGrowth/LeadCampaigns/Campaigns/${row.id}`),
        color: "primary",
        tooltip: "مشاهده",
      },
      {
        icon: <EditIcon sx={TABLE_ACTION_ICON_SX} />,
        onClick: (row) => {
          setEditingItem(row);
          setDialogOpen(true);
        },
        color: "secondary",
        tooltip: "ویرایش",
      },
    ],
    [navigate]
  );

  const handleSubmit = async (payload) => {
    setSaving(true);
    try {
      const res = editingItem
        ? await LeadCampaignApi.updateCampaign(editingItem.id, payload)
        : await LeadCampaignApi.createCampaign(payload);
      const normalized = LeadCampaignApi.normalize(
        res,
        editingItem ? "ویرایش کمپین ناموفق بود." : "ایجاد کمپین ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success(editingItem ? "کمپین ویرایش شد." : "کمپین ایجاد شد.");
      setDialogOpen(false);
      setEditingItem(null);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ذخیره" };
    } finally {
      setSaving(false);
    }
  };

  return (
    <MarketGrowthPageShell
      title="کمپین‌ها"
      description="ایجاد و مدیریت کمپین‌های جذب متقاضی با ارسال کنترل‌شده یا خودکار."
      loading={loading}
      error={error}
      onRetry={load}
    >
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingItem(null);
            setDialogOpen(true);
          }}
        >
          کمپین جدید
        </Button>
        <IconButton onClick={load}>
          <SyncIcon />
        </IconButton>
      </Box>
      <Card>
        <CardContent>
          <MarketGrowthDataTable
            rowKey="id"
            rows={tableRows}
            headCells={headCells}
            actions={tableActions}
            defaultSort={{ orderBy: "title", order: "asc" }}
          />
        </CardContent>
      </Card>
      <LeadCampaignFormDialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingItem}
        groups={groups}
        saving={saving}
      />
    </MarketGrowthPageShell>
  );
};

export default LeadCampaignsPage;
